import { LinkOutlined } from '@ant-design/icons'
import { useMetadata } from '@api/useMetadata'
import { useTanks } from '@api/useTanks'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { CreateTankDrawer } from '@components/Admin/Tank/Grid/CreateTankDrawer'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { BBDTag, GraviButton, GraviGrid, NotificationMessage, useThemeContext } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { RowExpanded } from './Grid/Detail/RowExpanded'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'tank'

const setupQueryParams = () => {
  const defaultFilter = {}
  const storeNumber = queryString.parse(window.location.search).store_number
  const tankId = queryString.parse(window.location.search).tank_id
  if (storeNumber) defaultFilter.store_number = storeNumber
  if (tankId) defaultFilter.tank_id = tankId
  return defaultFilter
}

export function Tank() {
  const [filters, setFilters] = useState(setupQueryParams())

  const { useTankOverviewQuery, updateTankMutation } = useTanks()
  const { data: tankOverviewData, status } = useTankOverviewQuery(filters)
  const { data: tankMetadata } = useMetadata('store/tank')
  const queryClient = useQueryClient()
  const [showingDrawer, setShowingDrawer] = useState(false)
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.site_write
  const { updateSetting } = useUserSettings()
  const gridApiRef = useRef()
  const columnDefs = useMemo(() => getColumnDefs(), [])
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(() => ({ getRowId }), [])
  const { currentTheme } = useThemeContext()
  const { mutateAsync: updateMutate } = updateTankMutation()

  // Create a context to pass to the grid
  const gridContext = useMemo(
    () => ({
      updateTankMutation: (tank) => updateMutate(tank),
      canEdit,
      tankMetadata,
    }),
    [updateMutate, canEdit, tankMetadata]
  )

  useEffect(() => {
    gridApiRef?.current?.forEachNode((node) => {
      node.setExpanded(false)
    })
  }, [currentTheme])

  return (
    <>
      <CreateTankDrawer metadata={tankMetadata} showingDrawer={showingDrawer} setShowingDrawer={setShowingDrawer} />
      <GraviGrid
        controlBarProps={{
          serverParams: tankOverviewData?.params,
          filters,
          setFilters,
          title: 'Tank Management',
          actionButtons: (
            <>
              <UploadDownloadButtons
                refreshCallback={() =>
                  queryClient.invalidateQueries({
                    queryKey: ['tankOverviewQuery'],
                  })
                }
                downloadEP={api.exportTanks}
                uploadEP={canEdit && api.importTanks}
                fileName='Tanks.xlsx'
              />
              {canEdit && (
                <GraviButton className='ml-3' theme2 buttonText='Create' onClick={() => setShowingDrawer(true)} />
              )}
            </>
          ),
        }}
        loading={status === 'pending'}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        detailRowAutoHeight
        detailCellRenderer={RowExpanded}
        externalRef={gridApiRef}
        masterDetail
        detailCellRendererParams={{
          tankMetadata,
        }}
        context={gridContext}
        storageKey={STORAGE_KEY}
        rowData={tankOverviewData?.rows}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </>
  )
}

const getColumnDefs = () => [
  {
    cellRenderer: 'agGroupCellRenderer',
    field: 'id',
    valueFormatter: () => '',
    headerName: '',
    maxWidth: 50,
  },
  {
    field: 'store_number',
    headerName: 'Site',
    comparator: (valueA, valueB) =>
      valueA?.localeCompare(valueB, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    cellRenderer: (params) => <b>{params?.value}</b>,
    filter: true,
    minWidth: 60,
  },
  {
    headerName: 'Manifold',
    field: 'manifold_id',
    cellRenderer: ({ value }) =>
      value &&
      value !== 'N/A' && (
        <BBDTag style={{ width: 'max-content', padding: '0 1em' }} theme2>
          <LinkOutlined className='pr-2' /> {value}
        </BBDTag>
      ),
    minWidth: 100,
    valueGetter: (params) => params?.data?.manifold_id || 'N/A',
  },
  {
    headerName: 'Tank ID',
    field: 'tank_id',
    filter: true,
    minWidth: 200,
  },
  {
    headerName: 'Product',
    field: 'product',
    minWidth: 250,
  },
  {
    headerName: 'Island',
    field: 'island',
    minWidth: 120,
  },
  {
    headerName: 'Carrier',
    field: 'carrier',
    minWidth: 60,
    valueGetter: (params) => params?.data?.carrier ?? 'N/A',
    cellRenderer: ({ value }) => (value && value !== 'N/A' ? value : null),
  },
  {
    headerName: 'Requires Pump',
    field: 'requires_pump',
    minWidth: 130,
    valueGetter: (params) => {
      return params.data?.requires_pump === true
    },
    cellRenderer: (params) => {
      if (params.value === true) {
        return <BBDTag theme2>Yes</BBDTag>
      }
      return null
    },
    filterValueFormatter: (params) => {
      return params.value ? 'Yes' : 'No'
    },
  },
  {
    headerName: 'Source System',
    field: 'source_system_id',
    minWidth: 60,
    hide: true,
  },
  {
    headerName: 'Source Id',
    field: 'source_id',
    minWidth: 60,
    hide: true,
  },
  {
    hide: true,
    headerName: 'Compliance Hold',
    field: 'compliance_hold',
    valueGetter: ({ data }) => (!!data?.compliance_hold ? 'Yes' : 'No'),
  },
  {
    hide: true,
    headerName: 'Hide Tank Alerts',
    field: 'hide_tank_alerts',
    minWidth: 120,
    cellRenderer: (params) => (params.value ? <BBDTag warning>Yes</BBDTag> : null),
    valueGetter: ({ data }) => (!!data?.hide_tank_alerts ? 'Yes' : 'No'),
  },
]
